import { useEffect, useState } from "react";
import { Menu, Sidebar, MenuItem } from "react-pro-sidebar";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import menuItems from "../data/sidebarData";
import { sidebarIcons } from "../data/sidebarIcons";
import { Box, IconButton, Tooltip } from "@mui/material";
import config from "../config/config";

const MOBILE_BREAKPOINT = 789;

// Design tokens from Figma ([Dev] EMS / Sidebar, node 44:3516)
const SIDEBAR_BG = "#272D37";
const ITEM_ACTIVE_BG = "#323844";
const ICON_ACTIVE = "#1BB29D";
const TEXT_ACTIVE = "#F9FAFA";
const TEXT_INACTIVE = "#C8CFDC";

const getVisibleItems = (section) => section?.items ?? [];

const AppSidebar = () => {
  const [collapsed, setCollapsed] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  // "broken" = below the mobile breakpoint (was provided by the removed useProSidebar hook)
  const [broken, setBroken] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  const [whiteLabelLogo, setWhiteLabelLogo] = useState(null);

  const user = useSelector((state) => state.user.user);

  const favIconPath = require(`../assets/images/saaslogo/${config.authOrAuthfavIcon}`);

  const collapseSidebar = () => setCollapsed((prev) => !prev);

  useEffect(() => {
    const handleResize = () => {
      const isBroken = window.innerWidth < MOBILE_BREAKPOINT;
      setBroken(isBroken);
      setCollapsed(isBroken);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // change the sidebar logo in the white label
  useEffect(() => {
    if (user?.whiteLabel && user?.logoImage) {
      const whitelabelImgUrl = `${config.apiUrl}/location-engine/media/get-logo/logo?imageUrl=${user?.logoImage}`;
      setWhiteLabelLogo(whitelabelImgUrl);
    }
  }, [user?.logoImage, user?.whiteLabel]);

  return (
    <Box className="no-scrollbar overflow-hidden"
      sx={{
        position: "sticky",
        display: "flex",
        height: "100vh",
        top: 0,
        bottom: 0,
        "& .ps-sidebar-root": {
          border: "none",
          zIndex: "0"
        },
        "& .ps-menu-button": {
          padding: "0 !important",
          height: "36px !important",
          color: `${TEXT_INACTIVE} !important`
        },
        "& .ps-menu-button:hover": {
          backgroundColor: `${ITEM_ACTIVE_BG} !important`,
        },
        "& .ps-menu-button:hover .icon-container": {
          color: `${ICON_ACTIVE} !important`
        },
        "& .ps-menu-button:hover .menu-item-text": {
          color: `${TEXT_ACTIVE} !important`,
          fontWeight: "500 !important",
        },
      }}
    >
      <Sidebar
        backgroundColor={SIDEBAR_BG}
        width="200px"
        collapsedWidth="64px"
        collapsed={collapsed}
      >
        <Menu>
          {/* Header */}
          <div className={`flex px-4 py-2 items-center gap-1 self-stretch h-[72px] ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {(!collapsed || broken) && (
              <div className="flex items-center gap-2 py-1 min-w-0">
                <p className="flex w-8 h-8 shrink-0 flex-col justify-center items-center rounded-full bg-[#3F4653]">
                  <img src={whiteLabelLogo || favIconPath} style={{ width: "18.667px", height: "16px", flexShrink: "0" }} alt={`${config.saasOrgName}-logo`} loading="lazy" />
                </p>
                <p className="text-[#F9FAFA] text-xs font-medium leading-4">{config.saasOrgName}</p>
              </div>
            )}

            {!broken && (
              <IconButton onClick={collapseSidebar} sx={{ color: TEXT_ACTIVE, p: '6px', borderRadius: "100px", flexShrink: 0, "&:hover": { bgcolor: ITEM_ACTIVE_BG } }}>
                <span className="flex w-5 h-5" style={{ transform: collapsed ? "scaleX(-1)" : "none" }}>
                  {sidebarIcons.panelClose}
                </span>
              </IconButton>
            )}
          </div>

          {/* Item Container */}
          <div className="h-[calc(100vh-128px)] no-scrollbar overflow-y-auto flex flex-col gap-3">
            {menuItems.map((menuSection, sectionIndex) => {
              const visibleItems = getVisibleItems(menuSection);
              return (
                <Box key={sectionIndex}>
                  {visibleItems?.map((menuItem) => {

                    return <MenuItem key={menuItem?.item}>
                      <Tooltip title={collapsed ? menuItem?.item : ''} placement="right"
                        componentsProps={{
                          tooltip: { sx: { color: "#fcfcfc", backgroundColor: "#565f70", padding: "5px 10px", borderRadius: "5px", fontWeight: "600", fontSize: "13px", cursor: "pointer" } }
                        }}
                      >
                        <NavLink
                          to={menuItem.link}
                          className={`w-full h-9 py-2 px-3 flex items-center gap-2 transition-all duration-300 no-underline ${collapsed && 'justify-center'}`}
                          style={({ isActive }) => ({
                            color: isActive ? TEXT_ACTIVE : TEXT_INACTIVE,
                            fontWeight: isActive ? "500" : "400",
                            background: isActive ? ITEM_ACTIVE_BG : "",
                            alignSelf: "stretch",
                            display: "flex",
                            alignItems: "center",
                          })}
                        >
                          {({ isActive }) => (
                            <>
                              <span className="icon-container flex w-5 h-5 shrink-0" id={menuItem?.item} style={{ color: isActive ? ICON_ACTIVE : undefined }}>
                                {menuItem?.icon}
                              </span>
                              {!collapsed && (
                                <span className="menu-item-text text-sm leading-5 truncate" id={menuItem?.item}>
                                  {menuItem?.item}
                                </span>
                              )}
                            </>
                          )}
                        </NavLink>
                      </Tooltip>
                    </MenuItem>
                  })}
                  {menuItems
                    ?.slice(sectionIndex + 1)
                    ?.some(section => getVisibleItems(section)?.length > 0) && (
                      <div className="h-[1px] bg-[#323844] my-3" />
                    )}
                </Box>
              )
            })}
          </div>

          {/* Vecmocon Branding footer */}
          <div className="px-3 py-4 border-t border-[#323844] h-[56px]">
            <div className={`flex flex-row items-center gap-2 ${collapsed ? 'justify-center' : 'justify-start'}`}>
              <img className="grayscale opacity-60 shrink-0" src={favIconPath} style={{ width: "18px", height: "16px" }} alt={`${config.saasOrgName}-logo`} />

              {!collapsed && <div className="flex flex-col">
                <div className="text-[#7B8495] text-[8px] font-medium leading-3">Powered by</div>
                <div className="text-[#7B8495] text-[10px] font-semibold leading-3">{config.saasOrgName}</div>
              </div>}
            </div>
          </div>
        </Menu>
      </Sidebar>
    </Box>
  );
};

export default AppSidebar;
